import { useState } from "react";
import { Bot, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/gestionale/integrations/supabase/client";
import { Button } from "@/gestionale/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/gestionale/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/gestionale/components/ui/dialog";
import { Badge } from "@/gestionale/components/ui/badge";

type Persona = { id: string; nome: string; cognome: string };

type Props = {
  persona: Persona;
  tipo: "atleta" | "giocatore";
};

export default function GeneraStatisticaButton({ persona, tipo }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<{ analisi: string; profilo: string } | null>(null);
  const [stats, setStats] = useState<Record<string, number>>({});

  const genera = async () => {
    setLoading(true);
    setAnalysis(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Sessione non valida");

      if (tipo === "atleta") {
        const { data, error } = await supabase
          .from("presenze")
          .select("presente, sessione:sessioni_allenamento(data, corso)")
          .eq("persona_id", persona.id);
        if (error) throw error;

        const sessioni = (data || []).filter((p: any) => p.sessione);
        const presenti = sessioni.filter((p: any) => !!p.presente);
        const corsi = new Set(sessioni.map((p: any) => p.sessione.corso).filter(Boolean));

        const summary = {
          presenze: presenti.length,
          sessioni: sessioni.length,
          percentuale: sessioni.length ? Math.round((presenti.length / sessioni.length) * 100) : 0,
          corsi: corsi.size,
        };
        setStats(summary);

        const ultime = [...sessioni]
          .sort((a: any, b: any) => String(a.sessione.data).localeCompare(String(b.sessione.data)))
          .slice(-8)
          .map((p: any) => ({
            data: p.sessione.data,
            corso: p.sessione.corso,
            presente: !!p.presente,
          }));

        const response = await fetch("/api/ia-articolo", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + session.access_token },
          body: JSON.stringify({
            modalita: "atleta",
            atleta: {
              nome: persona.nome + " " + persona.cognome,
              presenze: presenti.length,
              sessioni: sessioni.length,
              percentuale: summary.percentuale,
              corsi: Array.from(corsi),
              ultime,
            },
          }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || "Errore nell'analisi dell'atleta");
        setAnalysis(result);
      } else {
        const { data, error } = await supabase
          .from("statistiche_partita")
          .select("*, partita:partite(data, avversario, casa_trasferta, tipo, gol_fatti, gol_subiti)")
          .eq("persona_id", persona.id);
        if (error) throw error;

        const partite = (data || [])
          .filter((s: any) => s.partita)
          .sort((a: any, b: any) => String(a.partita.data).localeCompare(String(b.partita.data)));

        const summary = {
          presenze: partite.filter((s: any) => Number(s.minuti_giocati || 0) > 0).length,
          titolari: partite.filter((s: any) => !!s.titolare).length,
          minuti: partite.reduce((n: number, s: any) => n + Number(s.minuti_giocati || 0), 0),
          gol: partite.reduce((n: number, s: any) => n + Number(s.goal || 0), 0),
          ammonizioni: partite.reduce((n: number, s: any) => n + Number(s.ammonizioni || 0), 0),
          espulsioni: partite.reduce((n: number, s: any) => n + Number(s.espulsioni || 0), 0),
        };
        setStats(summary);

        const ultime = partite.slice(-5).map((s: any) => ({
          data: s.partita.data,
          avversario: s.partita.avversario,
          casa_trasferta: s.partita.casa_trasferta,
          minuti: Number(s.minuti_giocati || 0),
          titolare: !!s.titolare,
          gol: Number(s.goal || 0),
          ammonizioni: Number(s.ammonizioni || 0),
          espulsioni: Number(s.espulsioni || 0),
        }));

        const response = await fetch("/api/ia-articolo", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + session.access_token },
          body: JSON.stringify({
            modalita: "giocatore",
            giocatore: {
              nome: persona.nome + " " + persona.cognome,
              ...summary,
              ultime,
            },
          }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || "Errore nell'analisi del giocatore");
        setAnalysis(result);
      }

      setOpen(true);
    } catch (error: any) {
      toast.error(error.message || "Errore nella generazione della statistica");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={genera} disabled={loading}>
        {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Bot className="h-3.5 w-3.5 mr-1" />}
        {loading ? "Analizzo..." : "Genera statistica"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Statistica {tipo === "atleta" ? "atleta" : "giocatore"} · {persona.nome} {persona.cognome}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(stats).map(([key, value]) => (
                <Card key={key}>
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-xs text-muted-foreground capitalize">{key}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0 text-xl font-bold">{value}{key === "percentuale" ? "%" : ""}</CardContent>
                </Card>
              ))}
            </div>

            {analysis && (
              <>
                <Card>
                  <CardHeader><CardTitle className="text-base">Analisi IA</CardTitle></CardHeader>
                  <CardContent className="text-sm leading-6 whitespace-pre-wrap">{analysis.analisi}</CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-base">Profilo</CardTitle></CardHeader>
                  <CardContent className="text-sm leading-6 whitespace-pre-wrap">{analysis.profilo}</CardContent>
                </Card>
              </>
            )}

            <Badge variant="outline">Dati generati dalle statistiche registrate nel gestionale</Badge>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
